import type { VehicleRegistry } from '@hgc/core';

import { LinkManager, type LinkManagerOptions } from './LinkManager';
import { DEFAULT_LINK_CONFIG, type LinkConfig } from './linkConfig';

export interface AppStoreOptions {
  /** 注入链路管理器（测试用它接管传输与时钟） */
  readonly link?: LinkManager;
  readonly linkOptions?: LinkManagerOptions;
  /**
   * 启动时自动连接的配置。
   * `null` = 不自动连接 —— 测试、以及将来"离线界面"的启动方式都用它；
   * 省略 = 连内置模拟机（开发默认：没有真机也要能把整条链路和界面走通）。
   */
  readonly autoConnect?: LinkConfig | null;
}

/**
 * 应用级运行时容器。
 *
 * 它存在的理由是**生命周期**：链路的建立与销毁必须比任何一次路由切换、任何一个组件挂载都长。
 * 只要它还在，切到"设置"再切回"飞行"就只是重新订阅了一次，连接、会话、已归集的车辆状态
 * 一样都没丢。反过来，如果连接是由某个组件的 `useEffect` 建立的，上面那次切换就会
 * 走一遍断开→重连 —— 对真机是重新握手 + 丢掉这段时间的全部遥测，而界面上看不出来。
 *
 * 它是**模块级单例**（与 `@hgc/ui` 的主题 store 同一种做法）：不放进 React state 的原因是
 * 它自己就是"React 之外的那份真相"，再放进 state 就变成两份。
 */
export class AppStore {
  readonly link: LinkManager;

  private readonly autoConnect: LinkConfig | null;
  private started = false;
  private disposed = false;

  constructor(options: AppStoreOptions = {}) {
    this.link = options.link ?? new LinkManager(options.linkOptions ?? {});
    this.autoConnect = options.autoConnect === undefined ? DEFAULT_LINK_CONFIG : options.autoConnect;
  }

  /** 当前链路上的车辆注册表；未连接时为 null（界面必须处理这个 null） */
  get registry(): VehicleRegistry | null {
    return this.link.registry;
  }

  /**
   * 启动自动连接。幂等：React 的严格模式会把挂载副作用跑两遍，
   * 而"连两次"在真机上意味着两次握手 —— 这个幂等不是优化，是正确性。
   */
  start(): void {
    if (this.started || this.disposed) return;
    this.started = true;

    const config = this.autoConnect;
    if (config === null) return;
    // 连接失败会写进链路快照（界面显示原因），这里只兜住"已销毁"这类编程错误
    void this.link.connect(config).catch(() => undefined);
  }

  async dispose(): Promise<void> {
    if (this.disposed) return;
    this.disposed = true;
    await this.link.dispose();
  }
}

export function createAppStore(options: AppStoreOptions = {}): AppStore {
  return new AppStore(options);
}

let singleton: AppStore | null = null;

/**
 * 默认实例。**懒建**：导入本模块不应该产生任何副作用（不建定时器、不连链路），
 * 否则测试里 import 一下就多出一条后台定时器。
 */
export function getAppStore(): AppStore {
  singleton ??= new AppStore();
  return singleton;
}

/**
 * 丢弃模块级单例（测试专用）。返回被丢弃的实例，调用方负责 dispose 它 ——
 * 返回而不是就地销毁，是因为"谁创建谁清理"这条规则在测试里同样要成立。
 */
export function resetAppStore(): AppStore | null {
  const previous = singleton;
  singleton = null;
  return previous;
}
